import { ApiModels, PendoEvents, usePendo } from '@local/shared';
import { Flex, Icon } from '@servicetitan/anvil2';
import IconWork from '@servicetitan/anvil2/assets/icons/material/round/work.svg';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { FC, FocusEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useSearchStore } from '../../shared';
import { HighlightedText } from './highlighted-text';
import * as Styles from './search-result.module.less';

export const SearchResult: FC<{ caseResult: ApiModels.Case; resultIndex: number }> = observer(
    ({ caseResult, resultIndex }) => {
        const searchStore = useSearchStore();
        const pendoApi = usePendo();
        const linkRef = useRef<HTMLAnchorElement>(null);
        const isFocused = searchStore.focusedResultIndex === resultIndex;

        const text = useMemo(() => {
            return `Case #${caseResult.caseNumber} - ${caseResult.subject}`;
        }, [caseResult.caseNumber, caseResult.subject]);

        // Focus the link element when this result is focused
        useEffect(() => {
            if (isFocused && linkRef.current) {
                linkRef.current.focus();
            }
        }, [isFocused]);

        const trackNavigation = useCallback(() => {
            pendoApi.trackEvent(PendoEvents.CaseSearchResultNavigate, {
                caseNumber: caseResult.caseNumber,
            });
        }, [pendoApi, caseResult]);

        const handleFocus = () => {
            searchStore.setIsFocused(true);
        };

        const handleBlur = (event: FocusEvent<HTMLAnchorElement>) => {
            // If focus is moving to another search result link, do not reset focus, otherwise do it
            if (event.relatedTarget) {
                const relatedTarget = event.relatedTarget as HTMLElement;
                if (relatedTarget.matches('[data-ish-search-result-link]')) {
                    return;
                }
                // If going back to search input, keep results visible
                if (relatedTarget === searchStore.searchInputEl) {
                    return;
                }
            }
            searchStore.resetFocusedResult();
            // Only hide results if hideOnBlur is enabled (header mode)
            if (searchStore.hideOnBlur) {
                /*
                 * When tabbing away from results (not to search input or another result), hide results
                 * Delay to allow final interactions
                 */
                setTimeout(() => {
                    searchStore.setIsFocused(false);
                }, 100);
            }
        };

        // Handle keyboard navigation
        const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                searchStore.focusNextResult();
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                searchStore.focusPreviousResult();
            } else if (event.key === 'Escape') {
                // Reset focus to the search input
                event.preventDefault();
                searchStore.resetFocusedResult();
                searchStore.searchInputEl?.focus();
            } else if (event.key === 'Enter') {
                trackNavigation();
                // Let the default link behavior handle the navigation
            }
        };

        return (
            <Link
                ref={linkRef}
                to={`/case/${caseResult.caseNumber}`}
                className={classNames(Styles.link, {
                    [Styles.focused]: isFocused,
                })}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                onClick={trackNavigation}
                data-cy="ish-search-result-link"
                data-pendo="ish-search-result-link"
                data-ish-search-result-link="true"
                data-focused={isFocused ? '' : undefined}
            >
                <Flex direction="row" gap={2} alignItems="center" alignSelf="stretch">
                    <Flex className={Styles.iconContainer}>
                        <Icon
                            svg={IconWork}
                            style={{ width: '20px', height: '20px' }}
                            size="large"
                            inherit
                        />
                    </Flex>
                    <HighlightedText text={text} highlight={searchStore.query} />
                </Flex>
            </Link>
        );
    }
);
